import { useState } from 'react';
import { Kpi, Pnl, LineChart, ResultBadge, TokenCell, fmtUsd, fmtDay } from './anUi';

const MISES = [10, 50, 100, 250, 500, 1000];
const PERIODES = [['7d', '7 jours', 7], ['30d', '30 jours', 30], ['90d', '90 jours', 90], ['all', 'Tout', Infinity]];

// Simulation sur les positions FERMÉES uniquement (une position ouverte n'a pas de ROI réalisé).
export default function SimulatorTab({ d, walletName }) {
  const [mise, setMise] = useState(100);
  const [periode, setPeriode] = useState('all');
  const [simule, setSimule] = useState(false);

  const now = Date.now() / 1000;
  const maxAge = PERIODES.find(p => p[0] === periode)[2] * 86400;
  const trades = d.positions.filter(p => p.closed && p.roi != null && p.entreeTs && (now - p.entreeTs) <= maxAge);
  const sim = trades.map(p => ({ ...p, gainSim: mise * (p.roi / 100) }));
  const totalGain = sim.reduce((s, t) => s + t.gainSim, 0);
  const capitalFinal = mise + totalGain;
  const investTotal = mise * trades.length;
  const roi = investTotal > 0 ? (totalGain / investTotal) * 100 : 0;
  const wins = sim.filter(t => t.gainSim >= 0).length;
  const meilleur = sim.length ? sim.reduce((m, t) => (t.gainSim > m.gainSim ? t : m), sim[0]) : null;
  const pire = sim.length ? sim.reduce((m, t) => (t.gainSim < m.gainSim ? t : m), sim[0]) : null;
  let cap = mise;
  const courbe = [...sim].sort((a, b) => a.entreeTs - b.entreeTs).map(t => (cap += t.gainSim));

  return (
    <div className="an-stack">
      <div className="an-panel">
        <div className="an-section-title"><span className="an-section-icon">🎮</span>Simulateur de copytrade</div>
        <div className="an-muted">Si j'avais copié <b>{walletName}</b> avec une mise fixe, combien j'aurais gagné ?</div>
        <div className="an-sim-controls">
          <div>
            <div className="an-field-label">Mise par trade ($)</div>
            <div className="an-chips">
              {MISES.map(v => <button key={v} className={`an-chip${mise === v ? ' an-chip--active' : ''}`} onClick={() => setMise(v)}>${v}</button>)}
              <input className="an-input an-input--sm an-input--num" type="number" value={mise} placeholder="Libre" onChange={e => setMise(parseFloat(e.target.value) || 0)} />
            </div>
          </div>
          <div>
            <div className="an-field-label">Période</div>
            <div className="an-chips">
              {PERIODES.map(([k, l]) => <button key={k} className={`an-chip an-chip--alt${periode === k ? ' an-chip--active' : ''}`} onClick={() => setPeriode(k)}>{l}</button>)}
            </div>
          </div>
        </div>
        <button className="an-btn an-btn--accent an-btn--lg" onClick={() => setSimule(true)}>▶ LANCER LA SIMULATION</button>
      </div>

      {simule && (trades.length === 0
        ? <div className="an-empty-inline">Aucun trade fermé sur cette période.</div>
        : <>
            <div className="an-kpis">
              <Kpi label="Capital initial" value={`$${mise.toFixed(0)}`} />
              <Kpi label="Capital final" value={`$${capitalFinal.toFixed(0)}`} tone={capitalFinal >= mise ? 'pos' : 'neg'} />
              <Kpi label="Gain / Perte" value={fmtUsd(totalGain, true)} tone={totalGain >= 0 ? 'pos' : 'neg'} />
              <Kpi label="ROI simulé" value={<Pnl value={roi} />} />
              <Kpi label="Trades gagnants" value={wins} tone="pos" />
              <Kpi label="Trades perdants" value={sim.length - wins} tone="neg" />
              <Kpi label="Trades total" value={trades.length} />
            </div>
            <div className="an-sim-cards">
              {meilleur && <div className="an-sim-card an-sim-card--win"><div className="an-sim-card-title">🏆 Meilleur trade simulé</div><TokenCell p={meilleur} /><div className="an-small">Mise ${mise} → <b className="an-pnl--up">{fmtUsd(meilleur.gainSim, true)}</b> (ROI réel <Pnl value={meilleur.roi} digits={0} />)</div></div>}
              {pire && <div className="an-sim-card an-sim-card--loss"><div className="an-sim-card-title">💀 Pire trade simulé</div><TokenCell p={pire} /><div className="an-small">Mise ${mise} → <b className="an-pnl--down">{fmtUsd(pire.gainSim, true)}</b> (ROI réel <Pnl value={pire.roi} digits={0} />)</div></div>}
            </div>
            {courbe.length > 1 && (
              <div className="an-section">
                <div className="an-section-title"><span className="an-section-icon">📈</span>Évolution du capital simulé</div>
                <LineChart values={courbe} baseline={mise} height={90} />
                <div className="an-chart-foot"><span>Trade 1</span><span>Capital final <b className={capitalFinal >= mise ? 'an-pnl--up' : 'an-pnl--down'}>${capitalFinal.toFixed(0)}</b></span><span>Trade {trades.length}</span></div>
              </div>
            )}
            <div className="an-table-wrap an-table-wrap--scroll">
              <table className="an-table">
                <thead><tr><th>🪙 Token</th><th>💰 Mise</th><th>📈 ROI réel</th><th>💵 Gain sim</th><th>🏁 Résultat</th></tr></thead>
                <tbody>
                  {sim.map((t, i) => (
                    <tr key={`${t.mint}-${i}`} className="an-row">
                      <td><TokenCell p={t} /><div className="an-muted an-small">{fmtDay(t.entreeTs)}</div></td>
                      <td className="an-cell-num">${mise}</td>
                      <td className="an-cell-num"><Pnl value={t.roi} /></td>
                      <td className="an-cell-num"><span className={t.gainSim >= 0 ? 'an-pnl--up' : 'an-pnl--down'}>{fmtUsd(t.gainSim, true)}</span></td>
                      <td><ResultBadge resultat={t.gainSim >= 0 ? 'Gagnant' : 'Perdant'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>)}
    </div>
  );
}
